/**
 * Advisory Slice
 *
 * Reducers only — no additional-reducers map for async thunk action types.
 * Thunks (`advisoryThunks.ts`) dispatch these plain actions directly rather
 * than using `createAsyncThunk`, so there is nothing for `extraReducers` to
 * handle.
 *
 * `@reduxjs/toolkit` bundles Immer, so direct property assignment inside a
 * reducer body below produces a new, structurally-shared state tree — it is
 * not really "mutating" the previous state.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createAdvisoryEngagementEntry, initialAdvisoryState } from './advisoryInitialState';
import type { AdvisoryEngagementEntry, AdvisoryState } from './advisoryInitialState';
import { compareKeyQuestionOrder } from './advisoryTypes';
import type { EngagementSummaryDoc, KeyQuestionDoc, StrategicAdvisoryDoc } from './advisoryTypes';

/** The two per-project channel kinds (the third kind, `engagements`, has no `projectId`). */
type PerProjectChannelKind = 'goal' | 'keyQuestions';

/**
 * Returns the project's entry, lazily creating it via
 * `createAdvisoryEngagementEntry()` when absent. Every per-project reducer
 * below routes through this helper so the order listener callbacks arrive
 * in never matters — whichever channel resolves first creates the entry
 * for the others to fill in.
 */
function getOrCreateEntry(state: AdvisoryState, projectId: string): AdvisoryEngagementEntry {
  const existing = state.byProjectId[projectId];
  if (existing) {
    return existing;
  }
  const created = createAdvisoryEngagementEntry();
  state.byProjectId[projectId] = created;
  return created;
}

/**
 * Merges a freshly fetched (poll or `onSnapshot`) array into `existing` by
 * id, honouring the recently-mutated guard in BOTH directions — a guarded
 * id is neither resurrected (present in `fetched` but was just
 * optimistically removed) nor prematurely dropped (absent from `fetched`
 * because the write hasn't landed yet). An unguarded id present in
 * `fetched` replaces its local counterpart in place; an unguarded id absent
 * from `fetched` is dropped; a new, unguarded id is appended.
 * Module-private, non-exported — mirrors `getOrCreateEntry`'s own
 * visibility, and every branch is exercised by the reducer tests below.
 *
 * `now` is always supplied by the caller (the poll loop's `setTimeout`
 * callback, or the optimistic-remove thunk) — this function and every
 * reducer that calls it never reads `Date.now()` directly: a reducer must
 * not have side effects, and the same action dispatched twice must produce
 * the same result.
 */
function mergeSnapshotWithGuard<T extends { id: string; order?: number }>(
  existing: T[],
  fetched: T[],
  guard: Record<string, number>,
  now: number
): T[] {
  const fetchedById = new Map(fetched.map(item => [item.id, item]));
  const isGuarded = (id: string): boolean => guard[id] !== undefined && guard[id] > now;

  const kept = existing.filter(item => isGuarded(item.id) || fetchedById.has(item.id));
  const next = kept.map(item => (isGuarded(item.id) ? item : fetchedById.get(item.id) ?? item));
  for (const item of fetched) {
    if (!isGuarded(item.id) && !next.some(n => n.id === item.id)) {
      next.push(item);
    }
  }
  // This used to sort deterministically by `id` (lexical, `<`/`>` on the
  // strings). That was HALF a fix even then: the key-question collection
  // read applies no `orderBy`, so an unordered Firestore read returns
  // document-ID order, and sorting by id here made the poll path and a
  // reload AGREE with each other — but "agree with each other" is not the
  // same claim as "agree with the server's actual intended sequence". `id`
  // is an opaque identifier a client must never treat as a position; the
  // server's real answer order is a topological sort of the engagement's
  // dependency graph, now carried on the `order` field. Sorting by id
  // could — and, for any engagement whose ids don't happen to sort the
  // same way as their dependency order, DID — place a question before the
  // ones it depends on.
  //
  // `compareKeyQuestionOrder` (`advisoryTypes.ts`) is the one comparator
  // this feature uses for sequencing, shared with the render path
  // (`AdvisoryKeyQuestionEditor.tsx`) so the two can never drift. It is
  // still a stable sort, so this merge's own reconciliation behaviour (an
  // agent-added mid-session item and a fresh reload landing on the same
  // position) is unchanged in KIND — only the key changed, from `id` to the
  // server-authoritative `order`.
  return next.sort(compareKeyQuestionOrder);
}

/**
 * Drops every guard entry that has already lapsed at `now`. Without this,
 * `markRecentlyMutated` only ever writes and `recentlyMutated.keyQuestions`
 * grows monotonically for the life of the entry (cleared only by
 * `clearEngagement`/`resetAdvisory`), with every long-dead key re-scanned
 * by `mergeSnapshotWithGuard` on every 2 s tick. `now` is the
 * caller-supplied clock the merge reducers already receive, so this stays
 * as deterministic as the merge itself — the same action dispatched twice
 * still produces the same result.
 *
 * Mutates the Immer draft in place; only ever deletes keys that are already
 * expired, so it can never affect a merge decision made in the same tick.
 */
function pruneExpiredGuards(guard: Record<string, number>, now: number): void {
  for (const id of Object.keys(guard)) {
    if (guard[id] <= now) {
      delete guard[id];
    }
  }
}

// No `: Slice<AdvisoryState>` type annotation on the const below.
// Annotating the variable with `Slice<AdvisoryState>` leaves the `CaseReducers` generic at
// its default (`SliceCaseReducers<AdvisoryState>`, a `{ [K: string]:
// CaseReducer<State, PayloadAction<any>> }` index signature), which discards
// every reducer's SPECIFIC inferred payload type and widens every action
// creator's payload to `any` — including zero-payload reducers like
// `resetAdvisory`, whose action creator then requires a call-site argument
// under `tsc --strict` (`resetAdvisory()` fails "Expected 1 arguments, but
// got 0") despite the reducer itself taking no `action` parameter at all.
// Letting TypeScript infer the slice's type directly from this
// `createSlice(...)` call instead keeps every action creator's payload type
// exactly as narrow as its own reducer declares.
const advisorySlice = createSlice({
  name: 'advisory',
  initialState: initialAdvisoryState,
  reducers: {
    /**
     * Replaces the engagements list. Marks the channel's first snapshot as
     * received, derives `isEmpty` from the payload, and clears any stale
     * `errorCode` — a successful snapshot must clear a previous error on
     * this channel and no other.
     */
    setEngagements: (state, action: PayloadAction<EngagementSummaryDoc[]>) => {
      state.engagements = action.payload.filter(
        item => !state.deletedEngagementIds.includes(item.id)
      );
      state.engagementsChannel = {
        firstSnapshotReceived: true,
        isEmpty: state.engagements.length === 0,
        errorCode: null,
      };
    },

    /**
     * Records a denial/failure on the engagements-list channel. Touches
     * `engagementsChannel.errorCode` only — `byProjectId` is untouched,
     * since the engagements channel has no `projectId`.
     */
    setEngagementsError: (state, action: PayloadAction<string>) => {
      state.engagementsChannel.errorCode = action.payload;
    },

    /**
     * Replaces one project's goal document. Lazily creates the entry.
     * Marks `channels.goal`'s first snapshot as received, derives
     * `isEmpty` from whether the doc is `null`, and clears a stale
     * `errorCode` on that channel only.
     */
    setGoalDoc: (
      state,
      action: PayloadAction<{ projectId: string; doc: StrategicAdvisoryDoc | null }>
    ) => {
      const { projectId, doc } = action.payload;
      const entry = getOrCreateEntry(state, projectId);
      entry.goalDoc = doc;
      entry.channels.goal = { firstSnapshotReceived: true, isEmpty: doc === null, errorCode: null };
    },

    /**
     * Merges a freshly fetched key-questions snapshot into the project's
     * key-question list by id, through the recently-mutated guard. A full
     * replace would wipe an in-flight optimistic removal on every poll
     * tick, which is exactly the defect this reducer exists to close. This
     * is the ONE reducer both the local-mode poll branch AND the
     * `onSnapshot` branch dispatch through — both deliver "the full current
     * array" in the same shape, so treating them identically also protects
     * the `onSnapshot` path against a hypothetical future resurrection
     * race, not just the poll's known one. Writes the `channels.keyQuestions`
     * bookkeeping that `deriveChannelState` branches purely on, so the
     * empty-state routing depends on this staying correct.
     */
    mergeKeyQuestionsSnapshot: (
      state,
      action: PayloadAction<{ projectId: string; fetched: KeyQuestionDoc[]; now: number }>
    ) => {
      const { projectId, fetched, now } = action.payload;
      const entry = getOrCreateEntry(state, projectId);
      entry.keyQuestions = mergeSnapshotWithGuard(
        entry.keyQuestions,
        fetched,
        entry.recentlyMutated.keyQuestions,
        now
      );
      pruneExpiredGuards(entry.recentlyMutated.keyQuestions, now);
      entry.channels.keyQuestions = {
        firstSnapshotReceived: true,
        isEmpty: entry.keyQuestions.length === 0,
        errorCode: null,
      };
    },

    /**
     * Records a recently-mutated guard entry for one id on one project's
     * one channel — `expiresAt` is supplied by the caller (never
     * `Date.now()` read here). Dispatched alongside `removeKeyQuestion` at
     * an optimistic-remove call site, never from inside a merge reducer.
     */
    markRecentlyMutated: (
      state,
      action: PayloadAction<{
        projectId: string;
        kind: 'keyQuestions';
        id: string;
        expiresAt: number;
      }>
    ) => {
      const { projectId, kind, id, expiresAt } = action.payload;
      const entry = getOrCreateEntry(state, projectId);
      entry.recentlyMutated[kind][id] = expiresAt;
    },

    /**
     * Drops one id's guard entry outright, binding the guard's lifetime to
     * the WRITE it guards rather than to a wall-clock guess made at
     * dispatch time.
     *
     * Dispatched from the rollback branch of an optimistic remove: once the
     * delete has been refused, the document is back in the list AND still
     * on the server, so there is nothing left to hide — holding the guard
     * for the rest of the window would instead discard every legitimate
     * server-side update to that document until it lapsed.
     *
     * Deliberately does NOT lazily create the project entry: clearing a
     * guard on a project with no entry is a no-op, not a reason to
     * materialize state (contrast `markRecentlyMutated`, whose write has to
     * land somewhere).
     */
    clearRecentlyMutated: (
      state,
      action: PayloadAction<{ projectId: string; kind: 'keyQuestions'; id: string }>
    ) => {
      const { projectId, kind, id } = action.payload;
      const entry = state.byProjectId[projectId];
      if (entry) {
        delete entry.recentlyMutated[kind][id];
      }
    },

    /**
     * Upsert-by-id: replaces the key question with a matching id, else
     * inserts it — at `index` when the caller supplies one, otherwise
     * appending.
     *
     * `index` exists for the rollback path of an optimistic remove. Plain
     * `push` would return a rolled-back key question to the END of the
     * list, and this list is rendered with a visible `KQ n` marker and
     * positional aria-labels — so a FAILED removal of key question 2 of 4
     * would silently renumber the list and move the item to 4, reading as
     * "the removal succeeded and something else was added". A failed
     * removal must leave the list exactly as it found it.
     *
     * `index` is clamped into range, so a stale index (the list shrank while
     * the delete was in flight) degrades to an append rather than throwing
     * or leaving a hole.
     */
    upsertKeyQuestion: (
      state,
      action: PayloadAction<{ projectId: string; keyQuestion: KeyQuestionDoc; index?: number }>
    ) => {
      const { projectId, keyQuestion, index } = action.payload;
      const entry = getOrCreateEntry(state, projectId);
      const existingIndex = entry.keyQuestions.findIndex(q => q.id === keyQuestion.id);
      if (existingIndex >= 0) {
        entry.keyQuestions[existingIndex] = keyQuestion;
      } else if (index !== undefined && index >= 0) {
        entry.keyQuestions.splice(Math.min(index, entry.keyQuestions.length), 0, keyQuestion);
      } else {
        entry.keyQuestions.push(keyQuestion);
      }
    },

    /** Remove-by-id: filters out only the matching key question. */
    removeKeyQuestion: (state, action: PayloadAction<{ projectId: string; id: string }>) => {
      const { projectId, id } = action.payload;
      const entry = getOrCreateEntry(state, projectId);
      entry.keyQuestions = entry.keyQuestions.filter(q => q.id !== id);
    },

    /**
     * Records a denial/failure on exactly one per-project channel, never
     * leaking to another. Touches only `channels[kind].errorCode`
     * on the named project's entry — every other channel on that project,
     * every other project, and the engagements-list channel are all
     * structurally unreachable from this reducer body, so they cannot be
     * touched even by accident.
     */
    setChannelError: (
      state,
      action: PayloadAction<{ projectId: string; kind: PerProjectChannelKind; errorCode: string }>
    ) => {
      const { projectId, kind, errorCode } = action.payload;
      const entry = getOrCreateEntry(state, projectId);
      entry.channels[kind].errorCode = errorCode;
    },

    /** Deletes one project's entry entirely. Other entries and `engagements` are untouched. */
    clearEngagement: (state, action: PayloadAction<string>) => {
      delete state.byProjectId[action.payload];
    },

    /** Reconcile a server-confirmed soft delete without waiting for a list snapshot. */
    removeEngagement: (state, action: PayloadAction<string>) => {
      const projectId = action.payload;
      if (!state.deletedEngagementIds.includes(projectId))
        state.deletedEngagementIds.push(projectId);
      state.engagements = state.engagements.filter(item => item.id !== projectId);
      state.engagementsChannel.isEmpty = state.engagements.length === 0;
      delete state.byProjectId[projectId];
    },

    /**
     * Resets the entire advisory slice to `initialAdvisoryState`.
     *
     * (a) A global `LOGOUT` action handled in `rootReducer.ts` was
     *     rejected — it would reset `chat` and `user` too, which
     *     contradicts the project-level "leave chat untouched" decision.
     *     This reducer is scoped to the `advisory` slice only.
     * (b) This is the reset half of the teardown pair whose other half is
     *     `cleanupAllAdvisoryListeners()`. The only caller outside this
     *     file is `teardownAdvisorySession(dispatch)` in
     *     `advisorySession.ts` — the pair is structurally inseparable
     *     there, not by discipline here.
     * (c) It remains individually exported and individually unit-tested in
     *     this file — the pairing above is a call-site discipline, not an
     *     export-visibility rule.
     *
     * Takes no payload. Returning the module-level `initialAdvisoryState`
     * singleton is safe: Immer never mutates a reducer's *returned* value
     * in place on a later dispatch, it only drafts the *current* state
     * passed in, so this can never let a subsequent action mutate the
     * singleton itself.
     */
    resetAdvisory: () => initialAdvisoryState,
  },
});

export const {
  setEngagements,
  setEngagementsError,
  setGoalDoc,
  mergeKeyQuestionsSnapshot,
  markRecentlyMutated,
  clearRecentlyMutated,
  upsertKeyQuestion,
  removeKeyQuestion,
  setChannelError,
  clearEngagement,
  removeEngagement,
  resetAdvisory,
} = advisorySlice.actions;

export default advisorySlice.reducer;
